import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "../lib/api";
import { LastUpdated } from "../components/LastUpdated";
import { VideoCard } from "../components/VideoCard";
import { ArrowRight, Sparkles, Rss, GitBranch, Star, Database, Cpu, Bot, TrendingUp, Radio } from "lucide-react";

const StatBox = ({ icon: Icon, label, value, color = "cyan1" }) => (
  <div className="card p-5 flex items-center gap-4" data-testid={`stat-${label.toLowerCase().replace(/ /g, "-")}`}>
    <div className={`w-12 h-12 rounded-xl bg-${color}/10 border border-${color}/30 flex items-center justify-center`}>
      <Icon className={`w-5 h-5 text-${color}`} />
    </div>
    <div>
      <div className="font-mono text-[10px] uppercase tracking-widest text-slate-500">{label}</div>
      <div className="font-heading text-2xl font-black text-white">{value}</div>
    </div>
  </div>
);

export default function Home() {
  const [articles, setArticles] = useState([]);
  const [repos, setRepos] = useState([]);
  const [trending, setTrending] = useState(null);
  const [videos, setVideos] = useState([]);
  const [stats, setStats] = useState({ articles: 0, repositories: 0, total_stars: 0, ai_articles: 0 });

  useEffect(() => {
    api.get("/articles?limit=6").then((r) => setArticles(r.data));
    api.get("/repositories?limit=6").then((r) => setRepos(r.data));
    api.get("/repositories/trending?limit=3").then((r) => setTrending(r.data)).catch(() => {});
    api.get("/videos?limit=4").then((r) => setVideos(r.data)).catch(() => {});
    api.get("/stats").then((r) => setStats(r.data));
  }, []);

  const featured = articles[0];
  const rest = articles.slice(1, 5);

  return (
    <div className="relative z-10">
      {/* Live ticker */}
      <div className="border-y border-white/10 bg-void/60 overflow-hidden py-3">
        <div className="marquee text-sm font-mono">
          {[...Array(2)].map((_, k) => (
            <div key={k} className="flex items-center gap-12 shrink-0">
              <span className="chip-cyan chip"><Sparkles className="w-3 h-3" /> LIVE</span>
              <span className="text-slate-400">Llama 3.3 70B released by Meta</span>
              <span className="text-cyan1">•</span>
              <span className="text-slate-400">Claude Sonnet 4.6 leads SWE-Bench</span>
              <span className="text-magenta1">•</span>
              <span className="text-slate-400">Boston Dynamics Atlas goes fully electric</span>
              <span className="text-cyan1">•</span>
              <span className="text-slate-400">MCP adopted by OpenAI &amp; Google</span>
              <span className="text-magenta1">•</span>
              <span className="text-slate-400">Ollama adds Llama 3.2 Vision support</span>
              <span className="text-cyan1">•</span>
            </div>
          ))}
        </div>
      </div>

      {/* Hero */}
      <section className="relative overflow-hidden">
        <div className="grid-bg absolute inset-0 opacity-40 pointer-events-none" />
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-16 pb-12 relative">
          <div className="grid lg:grid-cols-12 gap-8 items-center">
            <div className="lg:col-span-7">
              <div className="chip chip-cyan mb-5" data-testid="hero-badge">
                <Bot className="w-3 h-3" /> Portal AI &amp; Robotics · 2026
              </div>
              <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-black tracking-tight leading-[1.05]">
                Berita <span className="gradient-text">AI</span> &amp; <span className="neon-text-cyan">Robotics</span>
                <br />+ Katalog <span className="neon-text-magenta">Agent</span> Open-Source
              </h1>
              <p className="mt-5 text-slate-400 text-lg max-w-2xl leading-relaxed">
                Kurasi harian tentang Llama, Claude, Ollama, LangChain, dan seluruh ekosistem agent AI. Baca berita, salin perintah instalasi, unduh repo — semua dalam satu portal siap-deploy.
              </p>
              <div className="mt-8 flex flex-wrap gap-3">
                <Link to="/articles" className="btn-primary" data-testid="hero-read-articles">
                  Baca Berita Terbaru <ArrowRight className="w-4 h-4" />
                </Link>
                <Link to="/repositories" className="btn-ghost" data-testid="hero-explore-catalog">
                  <GitBranch className="w-4 h-4" /> Jelajahi Repository
                </Link>
              </div>
              <div className="mt-8 flex flex-wrap gap-2">
                {["Ollama", "Claude", "Llama 3.3", "LangChain", "AutoGPT", "MCP"].map((t) => (
                  <Link key={t} to={`/repositories?search=${encodeURIComponent(t)}`} className="chip hover:border-cyan1/40 hover:text-cyan1 transition">
                    #{t}
                  </Link>
                ))}
              </div>
            </div>
            <div className="lg:col-span-5">
              <div className="relative">
                <div className="absolute -inset-1 bg-gradient-to-br from-cyan1/20 to-magenta1/20 rounded-3xl blur-2xl" />
                <img
                  src="https://images.unsplash.com/photo-1695144244472-a4543101ef35?crop=entropy&cs=srgb&fm=jpg&q=85&w=800"
                  alt="AI Neon"
                  className="relative w-full h-80 object-cover rounded-2xl border border-white/10"
                />
                <div className="absolute bottom-4 left-4 right-4 flex items-center gap-2">
                  <span className="chip chip-magenta"><Rss className="w-3 h-3" /> RSS · AI · Manual</span>
                  <span className="chip chip-green"><Sparkles className="w-3 h-3" /> AI Summary</span>
                </div>
              </div>
            </div>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-14">
            <StatBox icon={Database} label="Artikel" value={stats.articles} color="cyan1" />
            <StatBox icon={GitBranch} label="Repository" value={stats.repositories} color="magenta1" />
            <StatBox icon={Star} label="Total Stars" value={stats.total_stars.toLocaleString()} color="cyan1" />
            <StatBox icon={Sparkles} label="AI Articles" value={stats.ai_articles} color="magenta1" />
          </div>
          <LastUpdated className="mt-4" />
        </div>
      </section>

      {/* Trending Radar */}
      {trending?.repos?.length > 0 && (
        <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12" data-testid="trending-radar">
          <div className="mb-8">
            <div className="chip chip-magenta mb-3"><TrendingUp className="w-3 h-3" /> Radar Trending</div>
            <h2 className="font-heading text-3xl md:text-4xl font-bold">Repo Naik Daun Minggu Ini</h2>
            {trending.baseline && (
              <p className="text-xs text-slate-500 font-mono mt-2">Data pertumbuhan sedang dikumpulkan — sementara menampilkan repo terpopuler</p>
            )}
          </div>
          <div className="grid md:grid-cols-3 gap-6">
            {trending.repos.map((r, i) => (
              <Link key={r.id} to={`/repositories/${r.id}`} className="card p-6 group relative overflow-hidden" data-testid={`trending-repo-${i}`}>
                <div className="absolute top-4 right-4 font-heading text-5xl font-black text-white/5">#{i + 1}</div>
                <div className="flex items-center gap-2 mb-3">
                  <Cpu className="w-4 h-4 text-magenta1" />
                  <span className="chip">{r.category}</span>
                </div>
                <h3 className="font-heading text-xl font-bold group-hover:text-cyan1 transition">{r.name}</h3>
                <p className="text-sm text-slate-400 mt-2 line-clamp-2">{r.description}</p>
                <div className="mt-4 flex items-center gap-4 text-xs font-mono">
                  <span className="flex items-center gap-1 text-slate-400"><Star className="w-3 h-3 text-yellow-400" /> {r.stars?.toLocaleString()}</span>
                  {r.stars_delta_7d > 0 && (
                    <span className="chip chip-green flex items-center gap-1"><TrendingUp className="w-3 h-3" /> +{r.stars_delta_7d.toLocaleString()} / 7 hari</span>
                  )}
                </div>
              </Link>
            ))}
          </div>
        </section>
      )}

      {/* Video Signal */}
      {videos.length > 0 && (
        <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12" data-testid="home-videos">
          <div className="flex items-end justify-between mb-8">
            <div>
              <div className="chip chip-magenta mb-3"><Radio className="w-3 h-3" /> Signal · Video</div>
              <h2 className="font-heading text-3xl md:text-4xl font-bold">Video AI &amp; Tech Terbaru</h2>
            </div>
            <Link to="/videos" className="btn-ghost hidden sm:inline-flex" data-testid="home-videos-all">Semua Video <ArrowRight className="w-4 h-4" /></Link>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {videos.map((v) => <VideoCard key={v.id} video={v} compact />)}
          </div>
        </section>
      )}

      {/* Featured Articles */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="flex items-end justify-between mb-8">
          <div>
            <div className="chip chip-cyan mb-3"><TrendingUp className="w-3 h-3" /> Berita Pilihan</div>
            <h2 className="font-heading text-3xl md:text-4xl font-bold">Artikel Terbaru</h2>
          </div>
          <Link to="/articles" className="text-cyan1 text-sm font-medium flex items-center gap-1" data-testid="see-all-articles">
            Lihat semua <ArrowRight className="w-4 h-4" />
          </Link>
        </div>

        {featured && (
          <div className="grid lg:grid-cols-12 gap-6 mb-6">
            <Link to={`/articles/${featured.slug}`} className="lg:col-span-7 card group" data-testid={`featured-article-${featured.id}`}>
              <div className="h-72 md:h-96 overflow-hidden">
                {featured.image_url && (
                  <img src={featured.image_url} alt={featured.title} className="w-full h-full object-cover group-hover:scale-105 transition duration-500" />
                )}
              </div>
              <div className="p-6">
                <div className="flex gap-2 mb-3">
                  <span className="chip chip-cyan">{featured.category}</span>
                  {featured.ai_summary && <span className="chip chip-magenta"><Sparkles className="w-3 h-3" /> AI Summary</span>}
                </div>
                <h3 className="font-heading text-2xl md:text-3xl font-bold leading-tight">{featured.title}</h3>
                <p className="text-slate-400 mt-3 line-clamp-2">{featured.excerpt}</p>
                <div className="mt-4 text-xs font-mono uppercase tracking-widest text-slate-500">
                  {featured.reading_time} min read · {featured.source}
                </div>
              </div>
            </Link>
            <div className="lg:col-span-5 grid grid-cols-1 gap-4">
              {rest.slice(0, 3).map((a) => (
                <Link key={a.id} to={`/articles/${a.slug}`} className="card p-4 flex gap-4" data-testid={`article-card-${a.id}`}>
                  {a.image_url && <img src={a.image_url} alt="" className="w-24 h-24 object-cover rounded-lg" />}
                  <div className="flex-1 min-w-0">
                    <span className="chip chip-cyan text-[10px]">{a.category}</span>
                    <h4 className="font-heading font-bold mt-2 line-clamp-2">{a.title}</h4>
                    <div className="text-[10px] font-mono uppercase tracking-widest text-slate-500 mt-2">{a.reading_time} min · {a.source}</div>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        )}
      </section>

      {/* Featured Repos */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="flex items-end justify-between mb-8">
          <div>
            <div className="chip chip-magenta mb-3"><Cpu className="w-3 h-3" /> Trending Repositories</div>
            <h2 className="font-heading text-3xl md:text-4xl font-bold">AI Agent Catalog</h2>
          </div>
          <Link to="/repositories" className="text-magenta1 text-sm font-medium flex items-center gap-1" data-testid="see-all-repos">
            Semua repo <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          {repos.slice(0, 6).map((r) => (
            <Link key={r.id} to={`/repositories/${r.id}`} className="card p-6 flex flex-col" data-testid={`repo-card-${r.id}`}>
              <div className="flex items-start justify-between mb-3">
                <div>
                  <div className="font-heading text-lg font-bold">{r.name}</div>
                  <div className="font-mono text-[10px] text-slate-500 uppercase tracking-widest">{r.full_name}</div>
                </div>
                <span className="chip chip-green"><Star className="w-3 h-3" /> {r.stars?.toLocaleString() || 0}</span>
              </div>
              <p className="text-sm text-slate-400 line-clamp-3 flex-1">{r.description}</p>
              <div className="mt-4 flex gap-2 flex-wrap">
                <span className="chip">{r.category}</span>
                {r.language && <span className="chip chip-cyan">{r.language}</span>}
              </div>
            </Link>
          ))}
        </div>
      </section>
    </div>
  );
}
